import { useContentfulLiveUpdates } from '@contentful/live-preview/react';

import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { GatedRecentlyViewed } from '@/components/RecentlyViewed';
import type { StickyLayoutProps } from '@/components/StickyLayout';
import { StickyLayout } from '@/components/StickyLayout';
import type {
  ContentfulFooterFragment,
  ContentfulMainHeaderFragment,
  ContentfulSearchOverlayFragment,
} from '@/graphql/contentful/api';
import { DefaultLayoutDocument } from '@/graphql/contentful/api';
import { theme } from '@/theme/getTheme';

export type CMSLayoutDataProps = {
  footer?: ContentfulFooterFragment | null;
  header?: ContentfulMainHeaderFragment | null;
  search?: ContentfulSearchOverlayFragment | null;
};
export interface DefaultProps
  extends Pick<StickyLayoutProps, 'backgroundColor'> {
  /** BEC-24926: set by the product detail pages, which drop the banner. */
  alertBannerHiddenOnMobile?: boolean;
  children: React.ReactNode;
  cmsData?: CMSLayoutDataProps;
  draftMode?: boolean;
  /** Contentful page type; lets the Recently Viewed gate tell Editorial from Legal. */
  pageType?: string | null;
}

export const Default = ({
  alertBannerHiddenOnMobile,
  backgroundColor,
  children,
  cmsData,
  draftMode,
  pageType,
}: DefaultProps) => {
  const liveCmsData = useContentfulLiveUpdates(cmsData, {
    query: DefaultLayoutDocument,
  });

  return (
    <StickyLayout backgroundColor={backgroundColor}>
      {liveCmsData?.header && (
        <Header
          data-test-id='DefaultHeader'
          draftMode={draftMode ?? false}
          {...(liveCmsData?.header as ContentfulMainHeaderFragment)}
          alertBannerHiddenOnMobile={alertBannerHiddenOnMobile}
          cmsSearchOverlayData={liveCmsData.search}
        />
      )}
      <StickyLayout.Main
        data-test-id='DefaultStickyLayoutMain'
        headerBackground={
          liveCmsData?.header?.background ??
          theme.components.header.defaultBackground
        }
      >
        {children}
        {/* Above the footer; in _app it would land below it. The gate decides. */}
        <GatedRecentlyViewed pageType={pageType} />
      </StickyLayout.Main>
      {liveCmsData?.footer && (
        <Footer
          data-test-id='DefaultFooter'
          footerData={liveCmsData?.footer ?? {}}
        />
      )}
    </StickyLayout>
  );
};

Default.displayName = 'Default';
